import {
    CardItem,
    CollectionEvent,
    createCollectionAvailableEvent,
} from '@news-mono/web-common'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import React from 'react'
import { ListCard } from '../../cards/ListCard/ListCard'
import {
    StyledGrid,
    StyledGridColumn,
    StyledGridHeaderRow,
    StyledGridRow,
    StyledLandscapeItem,
    StyledList,
    StyledSectionHeader,
} from '../../collections/November/November.styled'
import { TimestampType } from '../../content/CardTimestamp/CardTimestamp'
import { SectionHeaderProps } from '../../section-header/SectionHeader/SectionHeader'
import { CardSwitcher } from '../../__helpers/CardSwitcher'
import { ImpressionAvailable } from '../../__helpers/impression-available-helper'
import { OverrideThemeSection } from '../../__helpers/override-theme-section'
import { ThemeMargins } from '../../__styling/settings/metrics'
import { Section } from '../../__styling/settings/sections'
import { ThemedRender } from '../../__styling/themed-render'
import { invertMaybeLoadedItems } from '../helpers/loading'

export interface NovemberProps {
    className?: string
    timestamp?: TimestampType
    hasBackground?: boolean
    verticalSpacing?: keyof ThemeMargins | undefined
    sectionHeader?: SectionHeaderProps
    section?: Section
    disableImpressionEvent?: boolean
    cardNumberOffset?: number
    noStretch?: boolean
    truncateListCards?: boolean
    onEvent: (event: CollectionEvent) => void
    expectedCards: number
    items: MaybeLoaded<CardItem[]>
    contentIsTangential?: boolean
}

export class November extends React.Component<NovemberProps> {
    static displayName = 'November'

    renderListCards() {
        const { timestamp, truncateListCards, onEvent } = this.props
        const items = invertMaybeLoadedItems(
            this.props.items,
            this.props.expectedCards,
        )

        return (
            <React.Fragment>
                {items.map((item, index) => {
                    const cardNum = index + 1
                    return (
                        <CardSwitcher
                            key={cardNum}
                            item={item}
                            cardContext="november-list"
                            cardNumber={cardNum}
                            publicationCard={(publicationItem) => (
                                <ListCard
                                    key={index}
                                    item={publicationItem}
                                    timestamp={timestamp}
                                    onEvent={onEvent}
                                    cardNumber={cardNum}
                                    isTruncated={truncateListCards}
                                />
                            )}
                        />
                    )
                })}
            </React.Fragment>
        )
    }

    renderLandscapeCards() {
        const { timestamp, onEvent } = this.props
        const items = invertMaybeLoadedItems(
            this.props.items,
            this.props.expectedCards,
        )

        return (
            <React.Fragment>
                {items.map((item, index) => {
                    const cardNum = index + 1
                    return (
                        <CardSwitcher
                            key={cardNum}
                            item={item}
                            cardContext="november-landscape"
                            cardNumber={cardNum}
                            publicationCard={(publicationItem) => (
                                <StyledLandscapeItem
                                    key={cardNum}
                                    item={publicationItem}
                                    mediaMode="hidden"
                                    asListElement
                                    cardNumber={
                                        cardNum +
                                        (this.props.cardNumberOffset || 0)
                                    }
                                    onEvent={onEvent}
                                    timestamp={timestamp}
                                    hideByline
                                    teaserMode="hidden"
                                />
                            )}
                        />
                    )
                })}
            </React.Fragment>
        )
    }

    renderSectionHeader() {
        const { sectionHeader } = this.props

        // Only render section header when there are items and it's specified
        return (
            sectionHeader &&
            (!this.props.items.loaded || this.props.items.result.length) && (
                <StyledGridHeaderRow canGrow={sectionHeader.canGrow}>
                    <StyledGridColumn>
                        <StyledSectionHeader
                            {...sectionHeader}
                            canGrow={sectionHeader.canGrow || true}
                        />
                    </StyledGridColumn>
                </StyledGridHeaderRow>
            )
        )
    }

    renderNovember() {
        const { className, verticalSpacing, contentIsTangential } = this.props
        return (
            <ImpressionAvailable
                loading={!this.props.items.loaded}
                available={() => {
                    if (this.props.disableImpressionEvent) {
                        return
                    }
                    if (!this.props.items.loaded) {
                        console.warn(
                            'Available should never be called when loading is true',
                        )
                        return
                    }
                    this.props.onEvent(
                        createCollectionAvailableEvent(
                            this.props.items.result,
                            'November',
                        ),
                    )
                }}
            >
                {(ref) => (
                    <StyledGrid
                        ref={ref}
                        className={className}
                        verticalSpacing={verticalSpacing}
                        noStretch={this.props.noStretch}
                        as={contentIsTangential ? 'aside' : 'div'}
                    >
                        {this.renderSectionHeader()}
                        <StyledGridRow hasBackground={this.props.hasBackground}>
                            <StyledGridColumn>
                                <StyledList
                                    removeBorderLast={this.props.hasBackground}
                                >
                                    <ThemedRender
                                        perthnow={() => this.renderListCards()}
                                        sevennews={() => this.renderListCards()}
                                        thewest={() =>
                                            this.renderLandscapeCards()
                                        }
                                    />
                                </StyledList>
                            </StyledGridColumn>
                        </StyledGridRow>
                    </StyledGrid>
                )}
            </ImpressionAvailable>
        )
    }

    render() {
        const { section } = this.props
        return section ? (
            <OverrideThemeSection section={section}>
                {this.renderNovember()}
            </OverrideThemeSection>
        ) : (
            this.renderNovember()
        )
    }
}
